import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Settings, Activity, Shield, AlertCircle, Stethoscope, Users, FileText, CreditCard, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Entitlement, fetchUserEntitlements } from '../../services/entitlements';
import { callGetPlanCatalog, callMintProductToken, callSyncMyEntitlementsFromPayments, readCachedPlanCatalog } from '../../services/functions';
import { productSsoUrl } from '../../config/productLinks';

type ProductCardProps = {
  id: 'vetaxis'|'staffaxis'|'noteaxis';
  name: string;
  description: string;
  domain: string; // e.g., vetaxis.bioaxis.co.za
  entitlement?: Entitlement;
  onLaunch: (id: 'vetaxis'|'staffaxis'|'noteaxis') => void;
  launching?: boolean;
};

const trialDaysLeft = (entitlement?: Entitlement): number | null => {
  const endsAt = entitlement?.trialEndsAt;
  if (!endsAt || typeof endsAt.toDate !== 'function') return null;
  const ms = endsAt.toDate().getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

const ProductCard: React.FC<ProductCardProps> = ({ id, name, description, domain, entitlement, onLaunch, launching }) => {
  const status = entitlement?.status || 'none';
  const plan = entitlement?.plan || 'none';
  const daysLeft = status === 'trialing' ? trialDaysLeft(entitlement) : null;
  const trialLapsed = (status === 'trialing' && daysLeft === 0) || (!!entitlement?.trialExpired && status !== 'active');
  const active = !trialLapsed && (status === 'active' || status === 'trialing');

  const getProductIcon = (productId: string) => {
    switch (productId) {
      case 'vetaxis': return Stethoscope;
      case 'staffaxis': return Users;
      case 'noteaxis': return FileText;
      default: return Activity;
    }
  };

  const ProductIcon = getProductIcon(id);

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className="glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass flex flex-col h-full"
    >
      {/* Product Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
            <ProductIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">{name}</h3>
            <p className="text-sm text-slate-600 dark:text-gray-400 mt-1">{description}</p>
          </div>
        </div>
      </div>

      {/* Status and Plan */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600 dark:text-gray-400">Plan:</span>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 capitalize">
            {plan}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-yellow-500'}`} />
          <span className={`text-sm font-medium ${
            active
              ? 'text-green-700 dark:text-green-400'
              : 'text-yellow-700 dark:text-yellow-400'
          }`}>
            {active ? (status === 'trialing' ? 'Trial' : 'Active') : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Trial countdown */}
      {active && status === 'trialing' && daysLeft !== null && (
        <div className="mb-4 px-3 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <span className="text-sm text-blue-700 dark:text-blue-300">
            {daysLeft === 1 ? 'Last day of your free trial' : `${daysLeft} days left in your free trial`}
          </span>
        </div>
      )}

      {/* Trial ended */}
      {trialLapsed && (
        <div className="mb-4 px-3 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">Trial period ended</span>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Your free trial of {name} has ended. Please subscribe to keep using it.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto space-y-3">
        {trialLapsed ? (
          <motion.a
            href="/plans"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            <CreditCard className="w-4 h-4" />
            Subscribe Now
          </motion.a>
        ) : (
        <motion.button
          onClick={() => onLaunch(id)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={!!launching}
          className={`w-full btn-primary flex items-center justify-center gap-2 ${launching ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {launching ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
              Launching…
            </span>
          ) : (
            <>
              <ExternalLink className="w-4 h-4" />
              Launch Application
            </>
          )}
        </motion.button>
        )}

        <motion.a
          href="/plans"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-slate-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-gray-300 font-medium hover:bg-white/70 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
        >
          <Settings className="w-4 h-4" />
          Manage Plan
        </motion.a>
      </div>

      {/* Domain */}
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-gray-700">
        <p className="text-xs text-slate-500 dark:text-gray-500 flex items-center gap-1">
          <Shield className="w-3 h-3" />
          {domain}
        </p>
      </div>
    </motion.div>
  );
};

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [ents, setEnts] = useState<Record<'vetaxis'|'staffaxis'|'noteaxis', Entitlement>>();
  const [error, setError] = useState<string|undefined>();
  const [loading, setLoading] = useState(true);
  const [launchingId, setLaunchingId] = useState<null | 'vetaxis'|'staffaxis'|'noteaxis'>(null);

  useEffect(() => {
    if (!user) return;
    // Warm the plan catalog cache so the Plans page renders instantly
    if (!readCachedPlanCatalog()) {
      Promise.resolve(callGetPlanCatalog()).catch(() => {});
    }
    let mounted = true;
    (async () => {
      try {
        // Self-heal in cases where payment succeeded but entitlement write was missed.
        try {
          await callSyncMyEntitlementsFromPayments();
        } catch (syncErr: any) {
          if (mounted) {
            setError(syncErr?.message || 'Payment sync failed. Please retry in a minute.');
          }
        }
        const e = await fetchUserEntitlements(user.uid);
        if (mounted) setEnts(e as any);
      } catch (err:any) {
        if (mounted) setError(err?.message || 'Failed to load entitlements');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  const launch = async (productId: 'vetaxis'|'staffaxis'|'noteaxis') => {
    setLaunchingId(productId);
    try {
      const { token } = await callMintProductToken({ productId });
      window.open(productSsoUrl(productId, token), '_blank', 'noopener');
    } catch (err:any) {
      setError(err?.message || 'Could not launch product');
    } finally {
      setLaunchingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          className="glass-effect rounded-2xl p-8 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 dark:text-gray-300">Loading your BioAxis suite...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            <span className="gradient-text">Your BioAxis</span> Suite
          </h1>
          <p className="text-lg text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
            Access all your healthcare management tools from one unified dashboard
          </p>
        </motion.div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex items-center gap-3 max-w-4xl mx-auto"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-4">
            <motion.a
              href="/billing"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary flex items-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Billing & Payments
            </motion.a>
            <motion.a
              href="/plans"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary flex items-center gap-2"
            >
              <Settings className="w-5 h-5" />
              Manage Plans
            </motion.a>
            {isAdmin && (
              <motion.a
                href="/admin"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary flex items-center gap-2"
              >
                <Shield className="w-5 h-5" />
                Admin Panel
              </motion.a>
            )}
          </div>
        </motion.div>

        {/* Product Grid */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <ProductCard
            id="vetaxis"
            name="VetAxis"
            description="Comprehensive veterinary practice management system"
            domain="vetaxis.bioaxis.co.za"
            entitlement={ents?.vetaxis}
            onLaunch={launch}
            launching={launchingId === 'vetaxis'}
          />
          <ProductCard
            id="staffaxis"
            name="StaffAxis"
            description="Healthcare staff management & scheduling platform"
            domain="app.staff.bioaxis.co.za"
            entitlement={ents?.staffaxis}
            onLaunch={launch}
            launching={launchingId === 'staffaxis'}
          />
          <ProductCard
            id="noteaxis"
            name="NoteAxis"
            description="Clinical documentation & note-taking solution"
            domain="noteaxis.bioaxis.co.za"
            entitlement={ents?.noteaxis}
            onLaunch={launch}
            launching={launchingId === 'noteaxis'}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
